/* =========================================================================
 * 日麻点数计算核心库
 * 算分逻辑参考开源项目 mahjong-calc 的 score.ts / fu.ts，按其授权 (MIT) 重写为纯 JS
 * 役判定为简化版，覆盖主流实战役 + 全部役满。
 * ========================================================================= */

// 牌的内部表示：'1m'..'9m', '1p'..'9p', '1s'..'9s', '1z'..'7z' (东南西北白发中)
// 0m / 0p / 0s 表示赤宝牌（按 5 计算形状）
window.MJ = (function () {
  const TILE_ORDER = [];
  for (const s of ['m', 'p', 's']) for (let n = 1; n <= 9; n++) TILE_ORDER.push(n + s);
  for (let n = 1; n <= 7; n++) TILE_ORDER.push(n + 'z');

  const Z_NAMES = ['東', '南', '西', '北', '白', '發', '中'];
  const SUIT_LABEL = { m: '萬', p: '筒', s: '索', z: '字' };

  // 牌 id -> 资源文件名（不含扩展名）
  function tileAsset(t) {
    if (t.endsWith('z')) {
      const n = Number(t[0]);
      return ['e', 's', 'w', 'n', 'p', 'f', 'c'][n - 1];
    }
    return t[0] + t[1]; // '1m', '5p', '0m'(赤五)
  }

  // 把赤五归一化到普通五（用于形状计算）
  function normalize(t) {
    if (t === '0m') return '5m';
    if (t === '0p') return '5p';
    if (t === '0s') return '5s';
    return t;
  }

  function isRed(t) { return t === '0m' || t === '0p' || t === '0s'; }
  function isHonor(t) { return t.endsWith('z'); }
  function isTerminal(t) {
    if (isHonor(t)) return false;
    const n = Number(t[0]);
    return n === 1 || n === 9;
  }
  function isYaochu(t) { return isHonor(t) || isTerminal(t); } // 幺九
  function suitOf(t) { return t[1]; }
  function numOf(t) { return Number(t[0]); }

  // 牌排序的 key
  function tileKey(t) {
    const norm = normalize(t);
    return TILE_ORDER.indexOf(norm) * 10 + (isRed(t) ? 1 : 0);
  }
  function sortTiles(arr) {
    return [...arr].sort((a, b) => tileKey(a) - tileKey(b));
  }

  // ============ 手牌分解：找到所有 (头 + 4 面子) 分解方式 ============
  // 输入：14 张归一化的牌（包括副露中已有的牌作为 fixed melds 已经扣掉）
  // 这里用最经典的递归分解，不要求最优，只列举所有可行分解
  function tilesToCounts(tiles) {
    const c = {}; // tile -> count
    for (const t of tiles) c[t] = (c[t] || 0) + 1;
    return c;
  }

  function _decomposeMelds(counts, suit) {
    // 递归地把同一花色（含 z）的牌分解为顺子/刻子的所有方式
    // suit: 'm'|'p'|'s'|'z'，z 中无顺子
    // 返回 [ [ {type:'shuntsu'|'kotsu', tile:'xx'}, ... ], ... ]
    const tiles = [];
    for (let n = 1; n <= 9; n++) {
      const k = n + suit;
      if (counts[k]) tiles.push(k);
    }
    if (tiles.length === 0) return [[]];
    const total = tiles.reduce((s, k) => s + counts[k], 0);
    if (total % 3 !== 0) return null;

    const results = [];
    // 找到第一张
    const first = tiles[0];
    const n = Number(first[0]);
    // 选项 A：作为刻子
    if (counts[first] >= 3) {
      counts[first] -= 3;
      const sub = _decomposeMelds(counts, suit);
      counts[first] += 3;
      if (sub) {
        for (const s of sub) results.push([{ type: 'kotsu', tile: first }, ...s]);
      }
    }
    // 选项 B：作为顺子起点（仅 m/p/s）
    if (suit !== 'z' && n <= 7) {
      const a = first, b = (n + 1) + suit, c = (n + 2) + suit;
      if (counts[a] && counts[b] && counts[c]) {
        counts[a]--; counts[b]--; counts[c]--;
        const sub = _decomposeMelds(counts, suit);
        counts[a]++; counts[b]++; counts[c]++;
        if (sub) {
          for (const s of sub) results.push([{ type: 'shuntsu', tile: a }, ...s]);
        }
      }
    }
    return results;
  }

  // 把 14 张正手牌（已减去副露）分解为 (头 + n 面子)，n = 4 - meldCount
  // 返回 [ {head, melds:[{type,tile,fromHand:true}, ...]}, ... ]
  function decomposeStandard(tiles, meldCountFromCalls) {
    const need = 4 - meldCountFromCalls; // 需要从手内组出多少面子
    const counts = tilesToCounts(tiles);
    const results = [];

    // 枚举头
    for (const t of TILE_ORDER) {
      if ((counts[t] || 0) >= 2) {
        counts[t] -= 2;
        // 按花色独立分解
        const suits = ['m', 'p', 's', 'z'];
        const perSuit = [];
        let ok = true;
        for (const su of suits) {
          const r = _decomposeMelds(counts, su);
          if (r === null) { ok = false; break; }
          perSuit.push(r);
        }
        counts[t] += 2;
        if (!ok) continue;
        // 笛卡尔积
        function cross(idx, acc) {
          if (idx === perSuit.length) {
            if (acc.length === need) {
              results.push({
                head: t,
                melds: acc.map(m => ({ ...m, fromHand: true }))
              });
            }
            return;
          }
          for (const opt of perSuit[idx]) cross(idx + 1, [...acc, ...opt]);
        }
        cross(0, []);
      }
    }
    // 去重
    const seen = new Set();
    const dedup = [];
    for (const r of results) {
      const sig = r.head + '|' + r.melds.map(m => m.type + m.tile).sort().join(',');
      if (!seen.has(sig)) { seen.add(sig); dedup.push(r); }
    }
    return dedup;
  }

  // 七对子判定
  function isChitoitsu(tiles) {
    if (tiles.length !== 14) return false;
    const c = tilesToCounts(tiles);
    const keys = Object.keys(c);
    return keys.length === 7 && keys.every(k => c[k] === 2);
  }

  // 国士判定
  function isKokushi(tiles) {
    if (tiles.length !== 14) return false;
    const yao = ['1m', '9m', '1p', '9p', '1s', '9s', '1z', '2z', '3z', '4z', '5z', '6z', '7z'];
    const c = tilesToCounts(tiles);
    let pairCount = 0;
    for (const y of yao) {
      const n = c[y] || 0;
      if (n === 0) return false;
      if (n === 2) pairCount++;
      if (n > 2) return false;
    }
    // 十三种全部存在，其中必有一个对子
    return pairCount === 1;
  }

  function isKokushi13(tiles, agariTile) {
    // 国士十三面：和牌时和的那张是十三种之一，且其余十三种各 1 张（即和牌成对那张 = agariTile）
    if (!isKokushi(tiles)) return false;
    const c = tilesToCounts(tiles);
    return (c[normalize(agariTile)] || 0) === 2;
  }

  // ============ 役 / 役满判定 ============
  // 输入 ctx：
  // {
  //   handTiles: [...]       // 14 张归一化牌（含和牌，含赤五已转成 5x）
  //   redCount: int          // 赤宝牌张数
  //   melds: [{type:'pon'|'chii'|'minkan'|'ankan', tiles:['1m','2m','3m']}, ...]  // 副露
  //   isClosed: bool         // 门前清（无非暗杠副露）
  //   agariTile: '5m'        // 和牌（归一化）
  //   by: 'tsumo'|'ron'
  //   roundWind: '1z'..'4z'
  //   seatWind: '1z'..'4z'
  //   riichi, doubleRiichi, ippatsu, rinshan, chankan, haitei, hotei, tenhou, chiihou
  //   doraIndicators, uraIndicators (用于 dora 计数)
  //   rule: { kuitanyao, akadora, kazoeYakuman, doubleWindFu }
  // }
  // 返回 { yaku:[{name,han}], yakuman:[{name,times}], han, fu, base, scores, name }

  // 把所有面子（来自手牌 + 副露）规范成统一形式
  function allBlocks(decomp, melds) {
    const blocks = [...decomp.melds.map(m => ({
      type: m.type, tile: m.tile, closed: true, fromHand: true
    }))];
    for (const m of melds) {
      if (m.type === 'chii') {
        const ts = m.tiles.map(normalize).sort((a, b) => numOf(a) - numOf(b));
        blocks.push({ type: 'shuntsu', tile: ts[0], closed: false });
      } else if (m.type === 'pon') {
        blocks.push({ type: 'kotsu', tile: normalize(m.tiles[0]), closed: false, isPon: true });
      } else if (m.type === 'minkan') {
        blocks.push({ type: 'kotsu', tile: normalize(m.tiles[0]), closed: false, isKan: true });
      } else if (m.type === 'ankan') {
        blocks.push({ type: 'kotsu', tile: normalize(m.tiles[0]), closed: true, isKan: true });
      }
    }
    return blocks;
  }

  function checkNormalYaku(decomp, ctx) {
    const blocks = allBlocks(decomp, ctx.melds);
    const head = decomp.head;
    const yaku = [];
    const yakuman = [];

    const allTiles = ctx.handTiles;
    const isClosed = ctx.isClosed;

    // ========== 役满 ==========
    // 大三元
    const sangenKotsu = blocks.filter(b => b.type === 'kotsu' && ['5z', '6z', '7z'].includes(b.tile));
    if (sangenKotsu.length === 3) yakuman.push({ name: 'daisangen', times: 1 });

    // 字一色
    if (allTiles.every(t => isHonor(t))) yakuman.push({ name: 'tsuiso', times: 1 });

    // 绿一色
    const greenSet = new Set(['2s', '3s', '4s', '6s', '8s', '6z']);
    if (allTiles.every(t => greenSet.has(t))) yakuman.push({ name: 'ryuiso', times: 1 });

    // 清老头
    if (allTiles.every(t => isTerminal(t))) yakuman.push({ name: 'chinroto', times: 1 });

    // 四暗刻
    const ankoCount = blocks.filter(b => b.type === 'kotsu' && b.closed).length;
    if (ankoCount === 4) {
      // 单骑听
      if (ctx.by === 'tsumo' || head === ctx.agariTile) {
        // 单骑：和牌牌 == 头
        if (head === ctx.agariTile) yakuman.push({ name: 'suanko-tanki', times: 1 });
        else yakuman.push({ name: 'suanko', times: 1 });
      } else {
        // 荣和的最后一刻只能算明刻；此分解已不成立，略
      }
    }

    // 大/小四喜
    const windKotsu = blocks.filter(b => b.type === 'kotsu' && ['1z', '2z', '3z', '4z'].includes(b.tile));
    if (windKotsu.length === 4) yakuman.push({ name: 'daisushi', times: 2 });
    else if (windKotsu.length === 3 && ['1z', '2z', '3z', '4z'].includes(head)) yakuman.push({ name: 'shosushi', times: 1 });

    // 四杠子
    const kanCount = blocks.filter(b => b.isKan).length;
    if (kanCount === 4) yakuman.push({ name: 'sukantsu', times: 1 });

    // 九莲宝灯（仅清一色 + 1112345678999 形）
    (function () {
      if (!isClosed) return;
      const suits = new Set(allTiles.map(suitOf));
      if (suits.size !== 1 || suits.has('z')) return;
      const su = [...suits][0];
      const c = {};
      for (const t of allTiles) c[t] = (c[t] || 0) + 1;
      const need = { [`1${su}`]: 3, [`9${su}`]: 3 };
      for (let n = 2; n <= 8; n++) need[`${n}${su}`] = 1;
      const cc = { ...c };
      // 减去 agariTile，看是否纯正
      const agari = ctx.agariTile;
      cc[agari] = (cc[agari] || 0) - 1;
      let pure = true;
      for (const k in need) {
        if (k === agari) continue;
        if ((cc[k] || 0) < need[k]) { pure = false; break; }
      }
      // 检查整体满足 1112345678999 + 1 张
      let ok = true;
      for (const k in need) if ((c[k] || 0) < need[k]) ok = false;
      if (!ok) return;
      // 多出的一张必须是 1-9
      const total = allTiles.length;
      if (total !== 14) return;
      // 检查没有别的牌
      let extra = 0;
      for (const k in c) {
        const base = need[k] || 0;
        extra += c[k] - base;
      }
      if (extra !== 1) return;
      if (pure) yakuman.push({ name: 'pure-churen', times: 1 });
      else yakuman.push({ name: 'churen', times: 1 });
    })();

    // 天和 / 地和
    if (ctx.tenhou) yakuman.push({ name: 'tenho', times: 1 });
    if (ctx.chiihou) yakuman.push({ name: 'chiho', times: 1 });

    if (yakuman.length > 0) return { yaku: [], yakuman, isYakuman: true };

    // ========== 普通役 ==========
    if (ctx.riichi && !ctx.doubleRiichi) yaku.push({ name: 'riichi', han: 1 });
    if (ctx.doubleRiichi) yaku.push({ name: 'double-riichi', han: 2 });
    if (ctx.ippatsu) yaku.push({ name: 'ippatsu', han: 1 });
    if (ctx.by === 'tsumo' && isClosed) yaku.push({ name: 'tsumo', han: 1 });
    if (ctx.rinshan) yaku.push({ name: 'rinshan', han: 1 });
    if (ctx.chankan) yaku.push({ name: 'chankan', han: 1 });
    if (ctx.haitei) yaku.push({ name: 'haitei', han: 1 });
    if (ctx.hotei) yaku.push({ name: 'hotei', han: 1 });

    // 役牌
    const headIsRoundWind = head === ctx.roundWind;
    const headIsSeatWind = head === ctx.seatWind;
    blocks.forEach(b => {
      if (b.type !== 'kotsu') return;
      if (b.tile === ctx.roundWind) yaku.push({ name: 'field-wind', han: 1 });
      if (b.tile === ctx.seatWind && b.tile !== ctx.roundWind) yaku.push({ name: 'seat-wind', han: 1 });
      else if (b.tile === ctx.seatWind && b.tile === ctx.roundWind) yaku.push({ name: 'seat-wind', han: 1 }); // 连风
      if (b.tile === '5z') yaku.push({ name: 'white', han: 1 });
      if (b.tile === '6z') yaku.push({ name: 'green', han: 1 });
      if (b.tile === '7z') yaku.push({ name: 'red', han: 1 });
    });

    // 断幺九
    if (allTiles.every(t => !isYaochu(t))) {
      if (isClosed || ctx.rule.kuitanyao) yaku.push({ name: 'tanyao', han: 1 });
    }

    // 平和
    (function () {
      if (!isClosed) return;
      if (blocks.some(b => b.type !== 'shuntsu')) return;
      // 头不能是役牌
      if (['5z', '6z', '7z'].includes(head)) return;
      if (head === ctx.roundWind || head === ctx.seatWind) return;
      // 待牌必须是两面
      // 简化判定：和牌可以替换分解中某顺子的边/中
      const agari = ctx.agariTile;
      let pinfu = false;
      for (const b of blocks) {
        if (b.type !== 'shuntsu') continue;
        if (suitOf(b.tile) !== suitOf(agari)) continue;
        const n = numOf(b.tile);
        const a = numOf(agari);
        if (a === n && n !== 7) pinfu = true; // 和起始张，等高一对
        if (a === n + 2 && n !== 1) pinfu = true; // 和末尾张，等低一对
      }
      if (pinfu) yaku.push({ name: 'pinfu', han: 1 });
    })();

    // 一杯口 / 二杯口
    if (isClosed) {
      const shun = blocks.filter(b => b.type === 'shuntsu').map(b => b.tile);
      const counts = {};
      for (const t of shun) counts[t] = (counts[t] || 0) + 1;
      const pairs = Object.values(counts).filter(v => v >= 2).length;
      if (pairs === 2) yaku.push({ name: 'ryampeko', han: 3 });
      else if (pairs === 1) yaku.push({ name: 'iipeko', han: 1 });
    }

    // 三色同顺
    (function () {
      const shun = blocks.filter(b => b.type === 'shuntsu');
      const byNum = {};
      for (const b of shun) {
        const n = numOf(b.tile);
        if (!byNum[n]) byNum[n] = new Set();
        byNum[n].add(suitOf(b.tile));
      }
      for (const n in byNum) {
        if (byNum[n].size === 3) {
          yaku.push({ name: 'sanshoku-dojun', han: isClosed ? 2 : 1 });
          return;
        }
      }
    })();

    // 三色同刻
    (function () {
      const ko = blocks.filter(b => b.type === 'kotsu' && !isHonor(b.tile));
      const byNum = {};
      for (const b of ko) {
        const n = numOf(b.tile);
        if (!byNum[n]) byNum[n] = new Set();
        byNum[n].add(suitOf(b.tile));
      }
      for (const n in byNum) {
        if (byNum[n].size === 3) yaku.push({ name: 'sanshoku-doko', han: 2 });
      }
    })();

    // 一气通贯
    (function () {
      const shun = blocks.filter(b => b.type === 'shuntsu');
      for (const su of ['m', 'p', 's']) {
        const ns = shun.filter(b => suitOf(b.tile) === su).map(b => numOf(b.tile));
        if (ns.includes(1) && ns.includes(4) && ns.includes(7)) {
          yaku.push({ name: 'ittsu', han: isClosed ? 2 : 1 });
          return;
        }
      }
    })();

    // 对对和
    if (blocks.every(b => b.type === 'kotsu')) yaku.push({ name: 'toitoi', han: 2 });

    // 三暗刻
    if (ankoCount === 3) yaku.push({ name: 'sananko', han: 2 });

    // 三杠子
    if (kanCount === 3) yaku.push({ name: 'sankantsu', han: 2 });

    // 小三元
    const sangenKotsu2 = blocks.filter(b => b.type === 'kotsu' && ['5z', '6z', '7z'].includes(b.tile));
    if (sangenKotsu2.length === 2 && ['5z', '6z', '7z'].includes(head)) {
      yaku.push({ name: 'shosangen', han: 2 });
    }

    // 混老头
    if (allTiles.every(t => isYaochu(t)) && blocks.every(b => b.type === 'kotsu')) {
      yaku.push({ name: 'honroto', han: 2 });
    }

    // 混全带幺九 / 纯全带幺九
    (function () {
      if (blocks.length === 0) return;
      const allBlockHasYao = (b) => {
        if (b.type === 'kotsu') return isYaochu(b.tile);
        const n = numOf(b.tile); // shuntsu 起始
        return n === 1 || n === 7;
      };
      if (!blocks.every(allBlockHasYao)) return;
      if (!isYaochu(head)) return;
      const hasShuntsu = blocks.some(b => b.type === 'shuntsu');
      if (!hasShuntsu) return; // 否则就是混老头
      const hasHonor = allTiles.some(isHonor);
      if (hasHonor) {
        yaku.push({ name: 'chanta', han: isClosed ? 2 : 1 });
      } else {
        yaku.push({ name: 'junchan', han: isClosed ? 3 : 2 });
      }
    })();

    // 混一色 / 清一色
    (function () {
      const nonHonor = allTiles.filter(t => !isHonor(t));
      const suits = new Set(nonHonor.map(suitOf));
      if (suits.size === 1) {
        const hasHonor = allTiles.some(isHonor);
        if (hasHonor) yaku.push({ name: 'honitsu', han: isClosed ? 3 : 2 });
        else yaku.push({ name: 'chinitsu', han: isClosed ? 6 : 5 });
      }
    })();

    // 七对子单独走
    return { yaku, yakuman: [], isYakuman: false };
  }

  function checkChitoitsuYaku(ctx) {
    const yaku = [];
    if (ctx.riichi && !ctx.doubleRiichi) yaku.push({ name: 'riichi', han: 1 });
    if (ctx.doubleRiichi) yaku.push({ name: 'double-riichi', han: 2 });
    if (ctx.ippatsu) yaku.push({ name: 'ippatsu', han: 1 });
    if (ctx.by === 'tsumo') yaku.push({ name: 'tsumo', han: 1 });
    if (ctx.haitei) yaku.push({ name: 'haitei', han: 1 });
    if (ctx.hotei) yaku.push({ name: 'hotei', han: 1 });
    yaku.push({ name: 'chitoitsu', han: 2 });
    if (ctx.handTiles.every(t => !isYaochu(t))) yaku.push({ name: 'tanyao', han: 1 });
    // 字一色
    if (ctx.handTiles.every(isHonor)) {
      return { yaku: [], yakuman: [{ name: 'tsuiso', times: 1 }], isYakuman: true };
    }
    // 混一/清一
    const nonHonor = ctx.handTiles.filter(t => !isHonor(t));
    const suits = new Set(nonHonor.map(suitOf));
    if (suits.size === 1) {
      const hasHonor = ctx.handTiles.some(isHonor);
      if (hasHonor) yaku.push({ name: 'honitsu', han: 3 });
      else yaku.push({ name: 'chinitsu', han: 6 });
    }
    if (ctx.handTiles.every(isYaochu)) yaku.push({ name: 'honroto', han: 2 });
    return { yaku, yakuman: [], isYakuman: false };
  }

  // ========= 符算 =========
  function calcFu(decomp, ctx) {
    let fu = 20;
    const detail = [{ name: 'futei', fu: 20 }];

    // 头符
    const head = decomp.head;
    let headFu = 0;
    if (['5z', '6z', '7z'].includes(head)) { headFu = 2; detail.push({ name: 'dragon-head', fu: 2 }); }
    else if (head === ctx.roundWind && head === ctx.seatWind) {
      headFu = ctx.rule.doubleWindFu || 2;
      detail.push({ name: 'double-wind-head', fu: headFu });
    } else if (head === ctx.roundWind) { headFu = 2; detail.push({ name: 'round-wind-head', fu: 2 }); }
    else if (head === ctx.seatWind) { headFu = 2; detail.push({ name: 'seat-wind-head', fu: 2 }); }
    fu += headFu;

    // 面子符
    const blocks = allBlocks(decomp, ctx.melds);
    for (const b of blocks) {
      if (b.type === 'shuntsu') continue;
      if (b.type === 'kotsu') {
        const isYao = isYaochu(b.tile);
        let bf = 0;
        if (b.isKan) {
          if (b.closed) bf = isYao ? 32 : 16;
          else bf = isYao ? 16 : 8;
        } else {
          if (b.closed) bf = isYao ? 8 : 4;
          else bf = isYao ? 4 : 2;
        }
        fu += bf;
        detail.push({
          name: (isYao ? 'yaochu-' : 'chunchan-') + (b.isKan ? (b.closed ? 'ankan' : 'minkan') : (b.closed ? 'anko' : 'minko')),
          tile: b.tile, fu: bf
        });
      }
    }

    // 待牌符（嵌张/边张/单骑 +2）
    const agari = ctx.agariTile;
    let waitFu = 0;
    let waitName = '';
    // 看 agari 是否是头（单骑）
    if (agari === decomp.head) {
      // 但只有当头是单骑等的，否则可能在刻子里
      // 简化：当头存在 agari 且没有其他面子能匹配，视为单骑
      const matchInMeld = decomp.melds.some(m => {
        if (m.type === 'kotsu') return m.tile === agari;
        if (m.type === 'shuntsu') {
          const n = numOf(m.tile);
          return suitOf(m.tile) === suitOf(agari) && (numOf(agari) === n || numOf(agari) === n + 1 || numOf(agari) === n + 2);
        }
        return false;
      });
      if (!matchInMeld) { waitFu = 2; waitName = 'tanki'; }
    }
    if (waitFu === 0) {
      // 边张 / 嵌张
      for (const m of decomp.melds) {
        if (m.type !== 'shuntsu') continue;
        if (suitOf(m.tile) !== suitOf(agari)) continue;
        const n = numOf(m.tile), a = numOf(agari);
        if (a === n + 1) { waitFu = 2; waitName = 'kanchan'; break; } // 嵌
        if ((n === 1 && a === 3) || (n === 7 && a === 7)) { waitFu = 2; waitName = 'penchan'; break; }
      }
    }
    if (waitFu > 0) { fu += waitFu; detail.push({ name: waitName, fu: waitFu }); }

    // 自摸 / 门前清荣和
    if (ctx.by === 'tsumo') { fu += 2; detail.push({ name: 'tsumo', fu: 2 }); }
    if (ctx.by === 'ron' && ctx.isClosed) { fu += 10; detail.push({ name: 'menzen-ron', fu: 10 }); }

    // 副露平和
    if (!ctx.isClosed && fu === 20 + headFu + waitFu && blocks.every(b => b.type === 'shuntsu')) {
      // 食い平和：直接 30
      fu = 30;
      detail.push({ name: 'kui-pinfu', fu: 30 - (20 + headFu + waitFu) });
    }

    // 平和自摸：固定 20 符
    const isPinfu = ctx.isClosed && blocks.every(b => b.type === 'shuntsu') &&
      !['5z', '6z', '7z', ctx.roundWind, ctx.seatWind].includes(head) && waitName === '';
    if (isPinfu && ctx.by === 'tsumo') {
      // 去掉 +2 自摸
      fu = 20;
    }

    // 向上取整到 10
    if (fu === 25) return { fu: 25, detail };
    fu = Math.ceil(fu / 10) * 10;
    return { fu, detail };
  }

  // ========= 基本点 / 各家分摊 =========
  function basePoint(fu, han, rule) {
    if (rule.kazoeYakuman && han >= 13) return 8000;
    if (han >= 11) return 6000;
    if (han >= 8) return 4000;
    if (han >= 6) return 3000;
    const raw = fu * Math.pow(2, 2 + han);
    if (han >= 5) return 2000;
    if (raw >= (rule.roundedMangan ? 1920 : 2000)) return 2000;
    return raw;
  }

  const ceil100 = n => Math.ceil(n / 100) * 100;

  // 计算最终点数 + 各家分摊
  function calcScore(fu, han, ctx, yakumanTimes = 0) {
    const isDealer = ctx.seatWind === '1z';
    let bp;
    if (yakumanTimes > 0) bp = 8000 * yakumanTimes;
    else bp = basePoint(fu, han, ctx.rule);

    const honba = ctx.honba || 0;
    const deposit = ctx.deposit || 0;
    let score = 0;
    let pay = {};

    if (ctx.by === 'ron') {
      const mult = isDealer ? 6 : 4;
      score = ceil100(bp * mult);
      pay = { ron: score + honba * 300 };
    } else {
      // 自摸
      if (isDealer) {
        const each = ceil100(bp * 2);
        score = each * 3;
        pay = { eachChild: each + honba * 100 };
      } else {
        const fromDealer = ceil100(bp * 2);
        const fromChild = ceil100(bp * 1);
        score = fromDealer + fromChild * 2;
        pay = { fromDealer: fromDealer + honba * 100, fromChild: fromChild + honba * 100 };
      }
    }
    const total = score + honba * 300 + deposit * 1000;
    return { base: bp, score, total, pay, honba, deposit };
  }

  // 限定满贯名
  function limitName(han, fu, rule, yakumanTimes) {
    if (yakumanTimes > 0) {
      const tuples = ['', '双倍', '三倍', '四倍', '五倍', '六倍'];
      return (tuples[yakumanTimes - 1] || '多重') + '役满';
    }
    if (rule.kazoeYakuman && han >= 13) return '役满';
    if (han >= 11) return '三倍满';
    if (han >= 8) return '倍满';
    if (han >= 6) return '跳满';
    const raw = fu * Math.pow(2, 2 + han);
    if (han >= 5) return '满贯';
    if (raw >= (rule.roundedMangan ? 1920 : 2000)) return '满贯';
    return null;
  }

  // ========= 顶层入口 =========
  function calculate(input) {
    // input.handTiles: 含赤五的手牌（含和牌张）
    // input.melds: 副露
    // input.agariTile: 和牌
    // 等等
    const rule = Object.assign({
      kuitanyao: true, akadora: true, kazoeYakuman: true,
      doubleWindFu: 4, roundedMangan: false
    }, input.rule || {});

    const isClosed = (input.melds || []).every(m => m.type === 'ankan');

    // 归一化
    const handNorm = input.handTiles.map(normalize);
    const meldsNorm = (input.melds || []).map(m => ({
      type: m.type,
      tiles: m.tiles.map(normalize)
    }));
    const agariNorm = normalize(input.agariTile);

    // 赤宝牌张数
    const redCount = (input.handTiles.filter(isRed).length) +
      (input.melds || []).reduce((s, m) => s + m.tiles.filter(isRed).length, 0);

    const ctx = {
      handTiles: handNorm,
      melds: meldsNorm,
      isClosed,
      agariTile: agariNorm,
      by: input.by,
      roundWind: input.roundWind,
      seatWind: input.seatWind,
      riichi: !!input.riichi,
      doubleRiichi: !!input.doubleRiichi,
      ippatsu: !!input.ippatsu,
      rinshan: !!input.rinshan,
      chankan: !!input.chankan,
      haitei: !!input.haitei,
      hotei: !!input.hotei,
      tenhou: !!input.tenhou,
      chiihou: !!input.chiihou,
      honba: input.honba || 0,
      deposit: input.deposit || 0,
      rule
    };

    // 立直只能门清
    if (ctx.riichi && !isClosed) ctx.riichi = false;
    if (ctx.doubleRiichi && !isClosed) ctx.doubleRiichi = false;
    if (ctx.ippatsu && !ctx.riichi && !ctx.doubleRiichi) ctx.ippatsu = false;

    // ========== 国士 ==========
    if (isClosed && isKokushi(handNorm)) {
      const k13 = isKokushi13(handNorm, agariNorm);
      const yakuman = [{ name: k13 ? 'kokushi-13' : 'kokushi', times: k13 ? 2 : 1 }];
      const times = yakuman.reduce((s, y) => s + y.times, 0);
      const score = calcScore(0, 0, ctx, times);
      return {
        kind: 'yakuman',
        yaku: [], yakuman,
        han: 0, fu: 0,
        ...score,
        name: limitName(0, 0, rule, times),
        decompose: { type: 'kokushi' }
      };
    }

    // ========== 七对 ==========
    let chitoitsuResult = null;
    if (isClosed && isChitoitsu(handNorm)) {
      const r = checkChitoitsuYaku(ctx);
      if (r.isYakuman) {
        const times = r.yakuman.reduce((s, y) => s + y.times, 0);
        const score = calcScore(0, 0, ctx, times);
        chitoitsuResult = { kind: 'yakuman', yaku: [], yakuman: r.yakuman, han: 0, fu: 0, ...score, name: limitName(0, 0, rule, times), decompose: { type: 'chitoitsu' } };
      } else {
        // 加宝牌
        const dora = countDora(handNorm, meldsNorm, input.doraIndicators || []);
        const ura = ctx.riichi ? countDora(handNorm, meldsNorm, input.uraIndicators || []) : 0;
        if (dora > 0) r.yaku.push({ name: 'dora', han: dora });
        if (ura > 0) r.yaku.push({ name: 'ura-dora', han: ura });
        if (rule.akadora && redCount > 0) r.yaku.push({ name: 'red-dora', han: redCount });
        const han = r.yaku.reduce((s, y) => s + y.han, 0);
        const score = calcScore(25, han, ctx);
        chitoitsuResult = { kind: 'normal', yaku: r.yaku, yakuman: [], han, fu: 25, ...score, name: limitName(han, 25, rule), decompose: { type: 'chitoitsu' } };
      }
    }

    // ========== 标准型 ==========
    const decomps = decomposeStandard(handNorm, meldsNorm.length);
    let best = null;
    for (const decomp of decomps) {
      const r = checkNormalYaku(decomp, ctx);
      if (r.isYakuman) {
        const times = r.yakuman.reduce((s, y) => s + y.times, 0);
        const score = calcScore(0, 0, ctx, times);
        const cand = { kind: 'yakuman', yaku: [], yakuman: r.yakuman, han: 0, fu: 0, ...score, name: limitName(0, 0, rule, times), decompose: decomp };
        if (!best || cand.total > best.total) best = cand;
        continue;
      }
      // 没役不能和
      if (r.yaku.length === 0) continue;

      // 加宝牌
      const dora = countDora(handNorm, meldsNorm, input.doraIndicators || []);
      const ura = ctx.riichi ? countDora(handNorm, meldsNorm, input.uraIndicators || []) : 0;
      const yaku = [...r.yaku];
      if (dora > 0) yaku.push({ name: 'dora', han: dora });
      if (ura > 0) yaku.push({ name: 'ura-dora', han: ura });
      if (rule.akadora && redCount > 0) yaku.push({ name: 'red-dora', han: redCount });

      const han = yaku.reduce((s, y) => s + y.han, 0);
      const fuObj = calcFu(decomp, ctx);
      const score = calcScore(fuObj.fu, han, ctx);
      const cand = {
        kind: 'normal', yaku, yakuman: [], han, fu: fuObj.fu, fuDetail: fuObj.detail,
        ...score, name: limitName(han, fuObj.fu, rule), decompose: decomp
      };
      if (!best || cand.total > best.total) best = cand;
    }

    if (chitoitsuResult && (!best || chitoitsuResult.total > best.total)) best = chitoitsuResult;

    if (!best) {
      return { error: 'no-yaku-or-shape' };
    }
    return best;
  }

  // 宝牌指示牌 -> 实际宝牌
  function indicatorToDora(ind) {
    const t = normalize(ind);
    if (t.endsWith('z')) {
      const n = numOf(t);
      if (n <= 4) return ((n % 4) + 1) + 'z'; // 东南西北循环
      return n === 7 ? '5z' : (n + 1) + 'z'; // 白发中循环 5->6->7->5
    }
    const n = numOf(t);
    return (n === 9 ? 1 : n + 1) + suitOf(t);
  }
  function countDora(handTiles, melds, indicators) {
    const targets = indicators.map(indicatorToDora);
    let n = 0;
    for (const t of handTiles) for (const dt of targets) if (t === dt) n++;
    for (const m of melds) for (const t of m.tiles) for (const dt of targets) if (t === dt) n++;
    return n;
  }

  // 暴露
  return {
    TILE_ORDER, Z_NAMES, SUIT_LABEL,
    tileAsset, normalize, isRed, isHonor, isTerminal, isYaochu,
    suitOf, numOf, sortTiles, tileKey,
    calculate, indicatorToDora,
    isChitoitsu, isKokushi
  };
})();
